import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  capture,
  getFeatureFlag,
  isExistingUser,
  markUserSeen,
} from '../../utils/analytics';
import { useLanguage } from '../../contexts/LanguageContext';
import ANNOUNCEMENTS from '../../config/announcements';
import './NewFeatureToast.css';

// Per-announcement dismissal flag. Sticky — once set we never re-show.
function dismissKey(id) {
  return `tm.dismissed.announce.${id}`;
}

// Per-announcement snooze. Stores a unix-ms timestamp; the toast skips
// itself until Date.now() crosses that point. Distinct from dismissal
// so a "Remind me later" doesn't permanently kill the toast.
function snoozeKey(id) {
  return `tm.snooze.announce.${id}`;
}

// Wait a beat after first paint so the toast isn't competing with the
// loading shimmer and so PostHog has time to fetch the flag.
const SHOW_DELAY_MS = 1500;

// How long "Remind me later" hides the toast.
const SNOOZE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

function readDismissed(id) {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(dismissKey(id)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(id) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(dismissKey(id), '1');
    }
  } catch {
    // ignore — Safari private mode etc.
  }
}

function isSnoozed(id) {
  try {
    if (typeof localStorage === 'undefined') return false;
    const raw = localStorage.getItem(snoozeKey(id));
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

function writeSnooze(id) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(snoozeKey(id), String(Date.now() + SNOOZE_DURATION_MS));
    }
  } catch {
    // ignore
  }
}

function readForcedIdFromUrl() {
  // `?announce=<id>` or legacy `?announce=1` (forces first eligible).
  try {
    const raw = new URLSearchParams(window.location.search).get('announce');
    if (!raw) return null;
    return raw;
  } catch {
    return null;
  }
}

/**
 * Find the first announcement that should render right now.
 * Returns null if nothing applies.
 *
 *   - Existing-user check is the hard gate — new users see none.
 *   - Dismissed entries are skipped.
 *   - `?announce=<id>` URL override bypasses the PostHog flag check
 *     (still respects the existing-user gate).
 *   - Otherwise PostHog flag `<entry.flag>` must be truthy.
 */
function pickAnnouncement(forcedRaw) {
  if (!isExistingUser()) return null;

  // Forced override mode — find the entry the URL pointed at. Snooze is
  // bypassed too (the user explicitly asked to see the toast); permanent
  // dismissal still wins.
  if (forcedRaw) {
    const targetId = forcedRaw === '1' ? null : forcedRaw;
    for (const entry of ANNOUNCEMENTS) {
      if (readDismissed(entry.id)) continue;
      if (targetId == null || entry.id === targetId) {
        return { entry, forced: true };
      }
    }
    return null;
  }

  // Normal mode — first eligible (flag on, not dismissed, not snoozed) wins.
  for (const entry of ANNOUNCEMENTS) {
    if (readDismissed(entry.id)) continue;
    if (isSnoozed(entry.id)) continue;
    const flagOn = getFeatureFlag(entry.flag);
    if (flagOn === true || flagOn === 'true') {
      return { entry, forced: false };
    }
  }
  return null;
}

/**
 * Bottom-right toast announcing a new feature to returning users.
 *
 * Rendering data is driven by `client/src/config/announcements.js` —
 * adding a new launch is "append config entry + add i18n keys + create
 * PostHog flag". No new component or route plumbing required.
 */
export default function NewFeatureToast() {
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const [active, setActive] = useState(null); // { entry, forced } | null

  useEffect(() => {
    // Read isExistingUser BEFORE we stamp the timestamp, so a brand-new
    // visitor still reads as new on their first session.
    const existing = isExistingUser();
    markUserSeen();
    if (!existing) return undefined;

    const forced = readForcedIdFromUrl();

    const timer = setTimeout(() => {
      const pick = pickAnnouncement(forced);
      if (pick) {
        setActive(pick);
        capture('feature_announcement.shown', {
          feature: pick.entry.id,
          forced: pick.forced,
        });
      }
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  if (!active) return null;

  const { entry } = active;
  const ns = `toast.${entry.i18nKey}`;

  const dismiss = (reason) => {
    writeDismissed(entry.id);
    setActive(null);
    capture('feature_announcement.dismissed', {
      feature: entry.id,
      reason,
    });
  };

  const snooze = () => {
    writeSnooze(entry.id);
    setActive(null);
    capture('feature_announcement.dismissed', {
      feature: entry.id,
      reason: 'snooze',
      snooze_days: SNOOZE_DURATION_MS / (24 * 60 * 60 * 1000),
    });
  };

  return (
    <div
      className="new-feature-toast"
      role="status"
      aria-live="polite"
      data-testid="new-feature-toast"
      data-announcement-id={entry.id}
    >
      <button
        type="button"
        className="new-feature-toast__close"
        aria-label={t('toast.close', 'Dismiss')}
        onClick={() => dismiss('close_button')}
      >
        ×
      </button>
      <div className="new-feature-toast__sparkle" aria-hidden="true">✨</div>
      <div className="new-feature-toast__title">
        {t(`${ns}.title`, `${ns}.title`)}
      </div>
      <div className="new-feature-toast__body">
        {t(`${ns}.body`, `${ns}.body`)}
      </div>
      <div className="new-feature-toast__actions">
        <button
          type="button"
          className="new-feature-toast__snooze"
          onClick={snooze}
        >
          {t('toast.remindLater', 'Remind me later')}
        </button>
        <Link
          className="new-feature-toast__cta"
          to={localizedPath(entry.path)}
          onClick={() => dismiss('cta_click')}
        >
          {t(`${ns}.cta`, 'Try it')}
          <span aria-hidden="true"> →</span>
        </Link>
      </div>
    </div>
  );
}
